import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import ViewOnlyBanner from "@/components/ViewOnlyBanner";
import { useCanEdit } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Package,
  Search,
  Upload,
  ImageIcon,
  ExternalLink,
  FileText,
  Paperclip,
} from "lucide-react";
import { formatMoney, CURRENCY_SYMBOLS } from "@/lib/format";
import { downloadAuthedFile } from "@/lib/downloadAuthedFile";
import { useConfirm } from "@/context/ConfirmContext";

const CATEGORIES = ["Materials", "Labor", "Parts", "Subcontract", "Other"];
const CURRENCIES = Object.keys(CURRENCY_SYMBOLS);

const empty = {
  name: "",
  sku: "",
  brand: "",
  category: "Materials",
  description: "",
  unit: "ea",
  cost: 0,
  price: 0,
  currency: "AUD",
  supplier_name: "",
  supplier_email: "",
  supplier_phone: "",
  supplier_url: "",
  notes: "",
  image_path: null,
  spec_sheets: [],
};

export function imageSrc(path) {
  if (!path) return null;
  const token = localStorage.getItem("sos_access_token") || "";
  return `${API}/files/${path}?auth=${encodeURIComponent(token)}`;
}

export default function Products() {
  const canEdit = useCanEdit();
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [uploadingSpec, setUploadingSpec] = useState(false);
  const fileInputRef = useRef(null);
  const specInputRef = useRef(null);

  const onUploadSpecSheet = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSpec(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        spec_sheets: [...(f.spec_sheets || []), { name: file.name, path: data.path }],
      }));
      toast.success("Spec sheet attached");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingSpec(false);
      if (specInputRef.current) specInputRef.current.value = "";
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter((p) => {
      if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q) ||
        (p.supplier_name || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      );
    });
  }, [list, query, categoryFilter]);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p });
    setOpen(true);
  };

  const onPickImage = () => fileInputRef.current?.click();

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      const payload = {
        ...form,
        cost: Number(form.cost) || 0,
        price: Number(form.price) || 0,
      };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    const ok = await confirm({
      title: `Delete ${p.name}?`,
      description: "This product will be removed from the catalogue.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24">
      <ViewOnlyBanner />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#0088CC] mb-2">
            Catalogue
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
            Products
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Materials, parts, labour rates and subcontract items ready to drop into a quote.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
            data-testid="new-product-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Product
          </Button>
        )}
      </div>

      <div className="sos-surface rounded-lg p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B6B7C]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, SKU, supplier…"
            className="pl-9 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
            data-testid="products-search-input"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger
            className="w-full sm:w-48 bg-white border-[#DDE5EC] text-[#011836]"
            data-testid="products-category-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && (
          <div className="text-[#5B6B7C] col-span-full text-sm">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="sos-surface rounded-lg col-span-full p-16 text-center">
            <Package className="h-10 w-10 text-[#B6C2CD] mx-auto mb-4" />
            <div className="text-sm text-[#5B6B7C]">
              {list.length === 0
                ? "No products yet — add your first catalogue item."
                : "No products match your filters."}
            </div>
          </div>
        )}
        {filtered.map((p) => (
          <div
            key={p.id}
            className="sos-surface sos-surface-hover rounded-lg overflow-hidden flex flex-col"
            data-testid={`product-card-${p.id}`}
          >
            <div className="aspect-[4/3] bg-white border-b border-[#DDE5EC] relative">
              {p.image_path ? (
                <img
                  src={imageSrc(p.image_path)}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <ImageIcon className="h-10 w-10 text-[#B6C2CD]" />
                </div>
              )}
              <div className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-black/70 backdrop-blur text-[#0088CC] border border-[#0088CC]/40">
                {p.category}
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-display text-base font-semibold text-[#011836] truncate">
                    {p.name}
                  </h3>
                  {(p.brand || p.sku) && (
                    <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] mt-0.5 truncate">
                      {[p.brand, p.sku ? `SKU ${p.sku}` : ""].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {canEdit && (
                    <>
                      <button
                        onClick={() => openEdit(p)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5]"
                        data-testid={`edit-product-${p.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => remove(p)}
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                        data-testid={`delete-product-${p.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {p.description && (
                <p className="text-xs text-[#5B6B7C] mt-2 line-clamp-2">{p.description}</p>
              )}

              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div>
                  <div className="text-[#5B6B7C] uppercase tracking-wider text-[10px]">Cost</div>
                  <div className="text-[#011836] font-display font-semibold mt-0.5 tabular-nums">
                    {formatMoney(p.cost, p.currency)}
                    <span className="text-[#5B6B7C] font-normal"> / {p.unit}</span>
                  </div>
                </div>
                <div>
                  <div className="text-[#5B6B7C] uppercase tracking-wider text-[10px]">Sell</div>
                  <div className="text-[#0088CC] font-display font-semibold mt-0.5 tabular-nums">
                    {formatMoney(p.price, p.currency)}
                  </div>
                </div>
              </div>

              {p.markup_pct != null && (
                <div className="mt-2 text-[10px] uppercase tracking-wider">
                  <span className="text-[#5B6B7C]">Markup </span>
                  <span className={p.markup_pct >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}>
                    {p.markup_pct.toFixed(1)}%
                  </span>
                </div>
              )}

              {(p.spec_sheets || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.spec_sheets.map((s, i) => (
                    <button
                      key={s.path}
                      type="button"
                      onClick={() => downloadAuthedFile(s.path, s.name || "spec-sheet")}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#0088CC]/30 bg-[#0088CC]/10 text-[#0088CC] text-[10px] hover:bg-[#0088CC]/20 transition-colors max-w-full"
                      title={`Download ${s.name || "spec sheet"}`}
                      data-testid={`spec-sheet-chip-${p.id}-${i}`}
                    >
                      <FileText className="h-2.5 w-2.5 shrink-0" />
                      <span className="truncate max-w-[140px]">{s.name || "Spec sheet"}</span>
                    </button>
                  ))}
                </div>
              )}

              {p.supplier_name && (
                <div className="mt-3 pt-3 border-t border-[#DDE5EC] text-xs">
                  <div className="text-[#5B6B7C] uppercase tracking-wider text-[10px]">
                    Supplier
                  </div>
                  <div className="text-[#011836] mt-0.5 truncate">{p.supplier_name}</div>
                  {p.supplier_url && (
                    <a
                      href={p.supplier_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#0088CC] hover:underline text-[11px] mt-0.5"
                    >
                      Open <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#DDE5EC] text-[#011836] max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="product-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <Package className="inline-block h-4 w-4 mr-2 text-[#0088CC]" />
              {editing ? "Edit product" : "New product"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Image upload */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Image</Label>
              <div className="mt-1.5 flex items-center gap-4">
                <div className="w-28 h-28 rounded border border-[#DDE5EC] bg-white overflow-hidden flex items-center justify-center relative shrink-0">
                  {form.image_path ? (
                    <img
                      src={imageSrc(form.image_path)}
                      alt="Product"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-[#B6C2CD]" />
                  )}
                </div>
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={onUploadImage}
                    data-testid="product-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onPickImage}
                    disabled={uploading}
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                    data-testid="upload-image-btn"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {uploading ? "Uploading…" : form.image_path ? "Replace image" : "Upload image"}
                  </Button>
                  {form.image_path && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm({ ...form, image_path: null })}
                      className="border-[#C4D0DB] text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC] hover:border-[#EF4444]"
                      data-testid="remove-image-btn"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                    </Button>
                  )}
                  <div className="text-[10px] text-[#5B6B7C]">PNG, JPG or WEBP · up to 8MB</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. 5083 Aluminium Plate 6mm"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">SKU</Label>
                <Input
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="product-sku-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Brand</Label>
                <Input
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  placeholder="e.g. Yamaha, Honda, Suzuki"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="product-brand-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Unit</Label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="ea, m, kg, hr…"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Currency</Label>
                <Select
                  value={form.currency}
                  onValueChange={(v) => setForm({ ...form, currency: v })}
                >
                  <SelectTrigger className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Cost (purchase)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.cost}
                  onChange={(e) => setForm({ ...form, cost: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="product-cost-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Sell price
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="product-price-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Description</Label>
              <Textarea
                value={form.description}
                rows={2}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>

            <div className="border-t border-[#DDE5EC] pt-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#0088CC] mb-3">
                Spec sheets
              </div>
              {(form.spec_sheets || []).length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {form.spec_sheets.map((s, i) => (
                    <div
                      key={s.path}
                      className="flex items-center justify-between gap-2 rounded border border-[#DDE5EC] bg-white px-3 py-1.5 text-xs"
                      data-testid={`spec-sheet-row-${i}`}
                    >
                      <button
                        type="button"
                        onClick={() => downloadAuthedFile(s.path, s.name || "spec-sheet")}
                        className="flex items-center gap-2 text-[#011836] hover:text-[#0088CC] min-w-0"
                        title="Download"
                      >
                        <FileText className="h-3.5 w-3.5 text-[#0088CC] shrink-0" />
                        <span className="truncate">{s.name || "Spec sheet"}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            spec_sheets: f.spec_sheets.filter((_, j) => j !== i),
                          }))
                        }
                        className="p-1 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC] shrink-0"
                        aria-label="Remove spec sheet"
                        data-testid={`remove-spec-sheet-${i}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={specInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={onUploadSpecSheet}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => specInputRef.current?.click()}
                disabled={uploadingSpec}
                className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836] text-xs"
                data-testid="attach-spec-sheet-btn"
              >
                <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                {uploadingSpec ? "Uploading…" : "Attach spec sheet"}
              </Button>
              <p className="text-[10px] text-[#5B6B7C] mt-1.5">PDF, Word, Excel or image · up to 12MB</p>
            </div>

            <div className="border-t border-[#DDE5EC] pt-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#0088CC] mb-3">
                Supplier
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Name</Label>
                  <Input
                    value={form.supplier_name}
                    onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                    className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                    data-testid="supplier-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Phone</Label>
                  <Input
                    value={form.supplier_phone}
                    onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })}
                    className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Email</Label>
                  <Input
                    type="email"
                    value={form.supplier_email}
                    onChange={(e) => setForm({ ...form, supplier_email: e.target.value })}
                    className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Website / link
                  </Label>
                  <Input
                    value={form.supplier_url}
                    onChange={(e) => setForm({ ...form, supplier_url: e.target.value })}
                    placeholder="https://…"
                    className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Notes</Label>
              <Textarea
                value={form.notes}
                rows={2}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="save-product-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

